import React, { useState, useEffect } from 'react'
import styles from './FileListView.module.css'
import { formatFileSize } from 'utils/general'
import CustomModal from 'components/CustomModal/CustomModal'
import { Modal } from 'react-bootstrap'

const FileListView = ({ files, onDelete, indexFile, maxVisibleFiles = 3, className = '' }) => {
  const [showAllFiles, setShowAllFiles] = useState(false)
  const [visibleFiles, setVisibleFiles] = useState([])
  const [hiddenFiles, setHiddenFiles] = useState([])

  useEffect(() => {
    if (!files || files.length === 0) {
      setVisibleFiles([])
      setHiddenFiles([])
      return
    }

    if (files.length <= maxVisibleFiles || showAllFiles) {
      setVisibleFiles(files)
      setHiddenFiles([])
    } else {
      setVisibleFiles(files.slice(0, maxVisibleFiles))
      setHiddenFiles(files.slice(maxVisibleFiles))
    }
  }, [files, maxVisibleFiles, showAllFiles])

  return (
    <>
      {/* styles for the Modal - specifically targeted to our modal-content classes only */}
      <style>
        {`
            .file-list-modal-dialog {
              margin: 1.75rem auto;
              justify-content: center;
            }
            
            .file-list-modal-content {
              border-radius: 16px;
              overflow: hidden;
              width: 80%;
            }
          `}
      </style>

      <div className={styles.filesContainer}>
        {visibleFiles.map((file, index) => (
          <FileItem key={index} file={file} onDelete={onDelete} indexFile={indexFile} className={className} />
        ))}

        {hiddenFiles.length > 0 && (
          <div
            className={styles.moreFilesIndicator}
            onClick={() => setShowAllFiles(true)}
            role="button"
            tabIndex={0}
            aria-label={`Show all ${files.length} files`}
          >
            {/* Background blurred file to show behind the overlay */}
            <div className={styles.blurredFileBackground}>
              <div className={styles.fileIcon}>
                <img src={`/assets/icons/${hiddenFiles[0].icon || 'file-x.svg'}`} alt="File" height="26" />
              </div>
              <div className={styles.fileDetails}>
                <span className={styles.fileNameContainer}>
                  <div className={styles.fileName}>
                    <span className={styles.truncateText}>
                      {hiddenFiles[0].name || hiddenFiles[0].path || hiddenFiles[0].fileName}
                    </span>
                  </div>
                </span>
              </div>
              <div className={styles.fileSize}>{formatFileSize(hiddenFiles[0].size)}</div>
            </div>
            {/* Overlay with the "+X files" text */}
            <div className={styles.moreFilesOverlay}>
              <div className={styles.moreFilesContent}>
                <span className={styles.moreFilesCount}>+{hiddenFiles.length}</span>
                <span className={styles.moreFilesText}>files</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <CustomModal
        show={showAllFiles}
        onHide={() => setShowAllFiles(false)}
        centered
        dialogClassName="file-list-modal-dialog"
        contentClassName="file-list-modal-content"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Project Files</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.filesList}>
            {files.map((file, index) => (
              <FileItem key={index} file={file} onDelete={onDelete} indexFile={indexFile} listView={true} />
            ))}
          </div>
        </Modal.Body>
      </CustomModal>
    </>
  )
}

const FileItem = ({ file, onDelete, indexFile, className = '', listView = false }) => {
  if (!file) return null

  const icon = file.icon || 'pdf-file.png'
  const fileName = file.path || file.name || file.fileName
  const text = fileName + (fileName === indexFile ? ' (index)' : '')
  const fileSize = typeof file.size === 'number' ? file.size : file.size

  if (listView) {
    return (
      <div className={styles.fileListItem}>
        <div className={styles.fileListIcon}>
          <img src={`/assets/icons/${icon}`} alt={file.type || 'File'} height="20" />
        </div>
        <div className={styles.fileListName}>
          <span className={styles.truncateText}>{text}</span>
        </div>
        {!!fileSize && <div className={styles.fileListSize}>{formatFileSize(fileSize)}</div>}
        <button
          type="button"
          className={styles.deleteListFile}
          onClick={(e) => {
            e.preventDefault()
            onDelete && onDelete(file)
          }}
          aria-label={`Delete file ${fileName}`}
        >
          <img height="20" width="20" src="/assets/icons/trash.svg" alt="delete" />
        </button>
      </div>
    )
  }

  return (
    <div className={`${styles.fileContainer} ${className}`}>
      <div className={styles.deleteContainer}>
        <button
          type="button"
          className={styles.deleteFile}
          onClick={(e) => {
            e.preventDefault()
            onDelete && onDelete(file)
          }}
          aria-label={`Delete file ${fileName}`}
        >
          <img src="/assets/icons/trash.svg" height="20" width="20" alt="delete" />
        </button>
      </div>
      <div className={styles.fileItem}>
        <div className={styles.fileIcon}>
          <img src={`/assets/icons/${icon}`} alt={file.type || 'File'} height="26" />
        </div>
        <div className={styles.fileDetails}>
          <div className={styles.fileNameContainer}>
            <div className={styles.fileName} title={text}>
              <span className={styles.truncateText}>{text}</span>
            </div>
          </div>
        </div>
        {!!fileSize && <div className={styles.fileSize}>{formatFileSize(fileSize)}</div>}
      </div>
    </div>
  )
}

export default FileListView
